import { Checkbox, Group } from '@any-ui-react/core';
import classNames from 'classnames';
import type { Key, ReactNode } from 'react';

interface Props<T> {
  value: T | null;
  onChange: (value: T | null) => void;
  items: Array<T>;
  label: (item: T) => ReactNode;
  title?: (item: T) => ReactNode;
  reactKey: (item: T) => Key;
  disabled?: boolean;
  error?: string;
}

export const SingleSelectChip = <T,>({
  value,
  onChange,
  items,
  label,
  title,
  reactKey,
  disabled = false,
  error,
}: Props<T>) => (
  <div>
    <Group className="flex-col gap-3" align="flex-start">
      {items.map(item => {
        const isChecked = value === item;

        return (
          <Checkbox.Card
            key={reactKey(item)}
            checked={isChecked}
            className={classNames('!border max-w-sm rounded px-4 py-2 transition-colors', {
              'cursor-pointer': !disabled,
              'cursor-not-allowed': disabled,
              '!border-blue-6 bg-blue-100': isChecked && !error,
              '!border-red-6 bg-red-50': error,
              '!border-gray-300 hover:!border-gray-400': !isChecked && !disabled,
              '!border-gray-200 bg-gray-100': disabled,
            })}
            disabled={disabled}
            value={String(reactKey(item))}
            onChange={checked => onChange(checked ? item : null)}
          >
            <Group align="flex-start" wrap="nowrap">
              <Checkbox.Indicator size="xs" />
              {title ? (
                <div>
                  <div className={classNames('leading-8 font-semibold', { 'text-blue-6': isChecked })}>
                    {title(item)}
                  </div>
                  <div className="leading-8 mt-1 text-gray-7 text-xs">{label(item)}</div>
                </div>
              ) : (
                <div className={classNames('leading-8', { 'text-blue-6': isChecked })}>{label(item)}</div>
              )}
            </Group>
          </Checkbox.Card>
        );
      })}
    </Group>
    {error && <p className="text-red-6 mt-1 text-xs">{error}</p>}
  </div>
);
